import type { UseQueryOptions } from "@pinia/colada";
import type { ObjectToCamel } from "ts-case-convert";

import { createError } from "#app";
import { objectToCamel } from "ts-case-convert";
import { toValue } from "vue";

import type {
  PartialContentCms,
  PartialSectionCms,
  ProductCms,
  ProductInfoCms,
} from "@solvari/statamic";
import type { LocaleMorpheus } from "@solvari/translations";

import {
  isError,
  networkErrorToSentry,
  NotFoundError,
  returnNetworkError,
  throwError,
  toSearchParams,
} from "@solvari/utils";

import type { StatamicPreview } from "~/shared/composables/useStatamicPreview.ts";
import type { NarrowedCmsResponse } from "~/shared/libs/narrowedCmsResponse.ts";

import {
  kyCacheTags,
  setPageUncacheable,
} from "~/shared/composables/useCacheTags.ts";
import { cmsCollectionEntriesUrl } from "~/shared/composables/useCmsApi.ts";
import { useStatamicPreview } from "~/shared/composables/useStatamicPreview.ts";

// Every key the page reads, plus `uri`: the only tag an ancestor rename purges a
// narrowed response through. Pinned in `tests/features/cmsNarrowedFields.spec.ts`.
const productFields = [
  "id",
  "sections",
  "jumplinks",
  "general",
  "geoProductCategory",
  "local_search",
  "seo",
  "variantSource",
  "title",
  "slug",
  "uri",
  "date",
  "last_modified",
  "updated_by",
] as const;

const productInfoFields = [
  "id",
  "sections",
  "jumplinks",
  "general",
  "geoProductCategory",
  "seo",
  "variantSource",
  "title",
  "uri",
  "date",
  "last_modified",
  "updated_by",
] as const;

type ProductPageResponse = NarrowedCmsResponse<
  ProductCms,
  typeof productFields
>;
type ProductInfoPageResponse = NarrowedCmsResponse<
  ProductInfoCms,
  typeof productInfoFields
>;

function useProductPage(
  params: { locale: LocaleMorpheus; uri: string },
  { previewToken, previewSite }: StatamicPreview = useStatamicPreview(),
) {
  const cmsUrl = cmsCollectionEntriesUrl("pages");

  return {
    key: () => ["cms", "product", params, toValue(previewToken)],
    query: throwError(async () => {
      const result = await returnNetworkError(async () =>
        objectToCamel(
          await kyCacheTags
            .get(cmsUrl, {
              searchParams: toSearchParams(
                {
                  fields: productFields.join(","),
                  filter: {
                    blueprint: "product",
                    site: previewSite.value ?? params.locale,
                    uri: params.uri,
                    status: previewToken.value ? "any" : undefined,
                  },
                  limit: 1,
                  token: previewToken.value,
                },
                { nestedKeys: true },
              ),
            })
            .json<ProductPageResponse>(),
        ),
      )();

      if (result instanceof NotFoundError) {
        return createError({
          statusCode: 404,
          statusMessage: "Page Not Found",
        });
      }
      if (isError(result)) {
        networkErrorToSentry(result);
        setPageUncacheable();

        return result;
      }

      const page = result.data[0];

      if (!page) {
        return createError({
          statusCode: 404,
          statusMessage: "Page Not Found",
        });
      }

      return page;
    }),
    staleTime: 5 * 60 * 1000,
  } satisfies UseQueryOptions;
}

function useProductInfoPage(
  params: { locale: LocaleMorpheus; uri: string },
  { previewToken, previewSite }: StatamicPreview = useStatamicPreview(),
) {
  const cmsUrl = cmsCollectionEntriesUrl("pages");

  return {
    key: () => ["cms", "productInfo", params, toValue(previewToken)],
    query: throwError(async () => {
      const result = await returnNetworkError(async () =>
        objectToCamel(
          await kyCacheTags
            .get(cmsUrl, {
              searchParams: toSearchParams(
                {
                  fields: productInfoFields.join(","),
                  filter: {
                    blueprint: "productInfo",
                    site: previewSite.value ?? params.locale,
                    uri: params.uri,
                    status: previewToken.value ? "any" : undefined,
                  },
                  limit: 1,
                  token: previewToken.value,
                },
                { nestedKeys: true },
              ),
            })
            .json<ProductInfoPageResponse>(),
        ),
      )();

      if (result instanceof NotFoundError) {
        return createError({
          statusCode: 404,
          statusMessage: "Page Not Found",
        });
      }
      if (isError(result)) {
        networkErrorToSentry(result);
        setPageUncacheable();

        return result;
      }

      const page = result.data[0];

      if (!page) {
        return createError({
          statusCode: 404,
          statusMessage: "Page Not Found",
        });
      }

      return page;
    }),
    staleTime: 5 * 60 * 1000,
  } satisfies UseQueryOptions;
}

type Section = ObjectToCamel<PartialSectionCms>["sections"][number];
type ContentBlock = ObjectToCamel<PartialContentCms>["content"][number];

export type { ContentBlock, Section };

export { productFields, productInfoFields, useProductInfoPage, useProductPage };
